import forbiddenWords from '@/assets/data/forbiddenWords.json';

// 錯誤訊息
export const CUSTOM_ERROR_CODE = {
  INVALID_IP_ADDRESS: 40001,
  STREAMING_ROOM_LOGIN_REQUIRED: 403011111, // 直播間需要登入
  STREAMING_NOT_AVAILABLE_IN_YOUR_COUNTRY: 40301, // 您目前不在收看地區
  STREAMING_NOT_FOUND: 40411,
  STREAMING_TYPE_NOT_SUPPORTED: 40412,
  INVALID_PHONE: 42201, // ＊電話號碼錯誤
  INVALID_TAX_ID: 42202, // ＊統一編號錯誤
  INVALID_AGE_CHECK_SELECTION: 42203,
  NOT_FOUND_USER_TICKETS: 42211,
  INVALID_USER_TICKETS: 42212, // 驗票失敗
  TICKET_HAS_BEEN_USED: 42212222, // 票卷已經使用
  NOT_FOUND_ACCOUNT_INFO: 42213,
  SC_QTY_NOT_ENOUGH: 42221,
  INFRAME_PAYMENT_SYSTEM_ERROR: 50001, // 系統繁忙，請稍後再試
};

// 禁止的特殊字元（避免使用 SQL、XSS 相關字符）
export const disallowedCharsRegex = /['<>\\;=()\s]/;

// 檢查是否包含禁用詞
export const isWordingValid = (value) => {
  return !forbiddenWords.some((word) => value.includes(word));
};

/**
 * Validate name field
 * @param {String} name - The name to validate
 * @param {Function} t - Vue I18n `$t()` function for translations
 * @returns {{ isValid: Boolean, message: String }}
 */
export const validateName = (name, t) => {
  // Check if name is missing or less than 2 characters
  if (!name || name.length < 2) {
    return { isValid: false, message: t('member-information.key069') };
  }
  // Check if name is too long
  if (name.length >= 20) {
    return { isValid: false, message: t('member-information.key067') };
  }
  // Check forbidden words and invalid characters
  if (!isWordingValid(name) || disallowedCharsRegex.test(name)) {
    return { isValid: false, message: t('member-information.key068') };
  }

  return { isValid: true, message: '' };
};

/**
 * Validate nickname field
 * @param {String} nickname - The nickname to validate
 * @param {Function} t - Vue I18n `$t()` function for translations
 * @returns {{ isValid: Boolean, message: String }}
 */
export const validateNickname = (nickname, t) => {
  // Check if nickname is too long (20 characters or more)
  if (nickname.length >= 20) {
    return { isValid: false, message: t('member-information.key067') };
  }
  // Check if nickname contains forbidden words
  if (!isWordingValid(nickname)) {
    return { isValid: false, message: t('member-information.key068') };
  }
  // Check if nickname contains disallowed special characters
  if (disallowedCharsRegex.test(nickname)) {
    return { isValid: false, message: t('member-information.key068') };
  }

  return { isValid: true, message: '' };
};

/**
 * Validate birth date
 * @param {String} dateStr - Input birth date string (YYYY-MM-DD)
 * @param {Function} t - $t function for i18n translation
 * @param {Object} options - Optional age limits
 * @returns {{ isValid: boolean, message: string, legalRep: boolean }}
 */
export const validateBirthDate = (dateStr, t, options = { minAge: 18, maxAge: 120 }) => {
  const errorMsg = t('member-information.key073');

  // 未填寫
  if (!dateStr) {
    return { isValid: false, message: errorMsg };
  }

  const birthDate = new Date(dateStr);
  const today = new Date();

  // 無效日期
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, message: errorMsg };
  }

  // 未來日期
  if (birthDate > today) {
    return { isValid: false, message: errorMsg };
  }

  // 計算年齡
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();
  const adjustedAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

  const legalRep = adjustedAge < 18;
  // 年齡太大 > 120
  if (adjustedAge > options.maxAge) {
    return { isValid: false, message: errorMsg };
  }
  // 未成年 < 18
  if (adjustedAge < options.minAge) {
    return { isValid: true, message: '', legalRep };
  }

  return { isValid: true, message: '' };
};

/**
 * Validate whether the provided email is in a valid format and not equal to the guardian email.
 * @param {string} email - The email address to validate.
 * @param {Function} t - The i18n translation function for localized messages.
 * @param {string} [guardianEmail] - Optional: legal guardian's email for comparison.
 * @returns {{ isValid: boolean, message: string }} - Validation result and error message (if any).
 */
export function validateEmail(email, t, guardianEmail = '') {
  if (!email || email.trim() === '') {
    return { isValid: false, message: t('member-information.key071') }; //＊ 請輸入有效電子信箱
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: t('member-information.key071') }; //＊ 請輸入有效電子信箱
  }

  if (guardianEmail && email.trim() === guardianEmail.trim()) {
    return { isValid: false, message: t('member-information.key100') }; //＊ 代理人信箱不得與本人信箱相同
  }

  return { isValid: true, message: '' };
}

/**
 * Check if email is duplicate with account or contactEmail
 * @param {String} email - Email to be verified
 * @param {String} accountEmail - User's account email
 * @param {String} contactEmail - User's contact email
 * @param {Function} t - i18n $t function
 * @returns {{ isValid: boolean, message: string }}
 */
export function validateLegalRepEmailUnique(email, accountEmail, contactEmail, t) {
  if (!email || email.trim() === '') {
    return { isValid: false, message: t('member-information.key071') }; //＊ 請輸入有效電子信箱
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: t('member-information.key071') }; //＊ 請輸入有效電子信箱
  }

  if (email === accountEmail || email === contactEmail) {
    return {
      isValid: false,
      message: t('member-information.key096'), // ＊法定代理人信箱不得與註冊帳號或聯絡信箱相同
    };
  }

  return { isValid: true, message: '' };
}
